import { Box, Paper, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import DashboardDataTable from 'modules/common/dashboardDataTable.component';
import PropTypes from 'prop-types';
import React from 'react';

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  noFiles: {
    padding: theme.spacing(3),
    textAlign: 'center',
  },
}));

const ClosedFilesTable = ({ files, totalCount, isLoading, page, rowsPerPage, onPageChange, onRowsPerPageChange }) => {
  const classes = useStyles();

  const handleRowClick = (fileId) => {
    window.open(`/authorizations/${fileId}`, '_blank', 'noopener,noreferrer');
  };

  // Closed files are paged server-side, so translate the grid's pagination state
  // changes back into the existing page/rowsPerPage callbacks
  const handlePaginationChange = (updater) => {
    const currentPagination = { pageIndex: page, pageSize: rowsPerPage };
    const nextPagination = typeof updater === 'function' ? updater(currentPagination) : updater;

    if (nextPagination.pageSize !== rowsPerPage) {
      onRowsPerPageChange({ target: { value: String(nextPagination.pageSize) } });
    } else if (nextPagination.pageIndex !== page) {
      onPageChange(null, nextPagination.pageIndex);
    }
  };

  const columns = [
    {
      accessorKey: 'fileNumber',
      header: 'File Number',
      Cell: ({ cell }) => cell.getValue() || '-',
    },
    {
      accessorKey: 'eeFullName',
      header: 'Employee',
      Cell: ({ cell }) => cell.getValue() || '-',
    },
    {
      accessorKey: 'clientName',
      header: 'Client',
      Cell: ({ cell }) => cell.getValue() || '-',
    },
    {
      accessorKey: 'statusDescription',
      header: 'Status',
      Cell: ({ cell }) => cell.getValue() || '-',
    },
    {
      accessorKey: 'closedDate',
      header: 'Closed Date',
      Cell: ({ cell }) => (cell.getValue() ? new Date(cell.getValue()).toLocaleDateString() : '-'),
    },
  ];

  if (isLoading) {
    return (
      <Paper elevation={0} className={classes.tableContainer}>
        <Typography className={classes.noFiles} color="textSecondary">
          Loading files...
        </Typography>
      </Paper>
    );
  }

  if (files.length === 0) {
    return (
      <Paper elevation={0} className={classes.tableContainer}>
        <Typography className={classes.noFiles} color="textSecondary">
          No closed files found.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box className={classes.tableContainer}>
      <DashboardDataTable
        columns={columns}
        data={files}
        initialSorting={[{ id: 'closedDate', desc: true }]}
        manualPagination
        rowCount={totalCount}
        state={{ pagination: { pageIndex: page, pageSize: rowsPerPage } }}
        onPaginationChange={handlePaginationChange}
        muiTableBodyRowProps={({ row }) => ({
          onClick: () => handleRowClick(row.original.fileId),
          sx: { cursor: 'pointer' },
          hover: true,
        })}
      />
    </Box>
  );
};

ClosedFilesTable.propTypes = {
  files: PropTypes.arrayOf(
    PropTypes.shape({
      fileId: PropTypes.number.isRequired,
      fileNumber: PropTypes.string,
      eeFullName: PropTypes.string,
      clientName: PropTypes.string,
      statusDescription: PropTypes.string,
      closedDate: PropTypes.string,
    }),
  ).isRequired,
  totalCount: PropTypes.number.isRequired,
  isLoading: PropTypes.bool.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
};

export default ClosedFilesTable;
